import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { api, downloadCsv, errorMessage, isCode } from "../api/client.ts";
import type { List, Payout } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { DateRange, FilterBar, useUrlFilters } from "../components/Filters.tsx";
import { IconDownload, IconPlus } from "../components/Icons.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, AssetChip, Details, EmptyState, ExternalLink, Hash, PageHeader, Player, Spinner, StatusPill, Time } from "../components/ui.tsx";
import { useAssets } from "../lib/assets.ts";
import { chainName, formatTimeFull, exactUnits, formatUnits, tokenOf } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { usePagedList } from "../lib/paging.ts";
import { Pager } from "../components/Pager.tsx";
import { useMe, useSession } from "../lib/session.tsx";
import { NewPayout } from "./NewPayout.tsx";
import { useTelegram } from "../components/Telegram.tsx";
import { fmt, tr, useT } from "../i18n/index.tsx";

const STATUSES = ["pending_approval", "queued", "broadcast", "completed", "failed", "rejected"];

/** Translated reason a queued payout is held; the server's English sentence is the fallback (and the tooltip). */
function waitingText(p: Payout): string {
  const codes = tr().vault.waitingCodes as Record<string, string>;
  const label = p.waiting_code ? codes[p.waiting_code] : undefined;
  return label ?? (p.waiting_reason ?? "").replace(/^./, (c) => c.toUpperCase()).replace(/\.$/, "");
}

export function Payouts() {
  const t = useT();
  const me = useMe();
  const { can } = useSession();
  const { values, set, setMany } = useUrlFilters(["q", "status", "asset", "from", "to"]);
  const assets = useAssets();
  const list = usePagedList<Payout>("/payouts", values);
  const telegram = useTelegram();
  const waiting = useAsync(() => api.get<List<Payout>>("/payouts", { status: "pending_approval", limit: 100 }), []);
  const [open, setOpen] = useState<Payout | null>(null);
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  // ?open=<id> (from the statement or the topbar lookup) opens that payout's drawer.
  const [sp] = useSearchParams();
  const openId = sp.get("open");
  useEffect(() => {
    if (!openId) return;
    let alive = true;
    api.get<Payout>(`/payouts/${encodeURIComponent(openId)}`).then((p) => alive && setOpen(p)).catch((e) => alive && toast(errorMessage(e), "error"));
    return () => {
      alive = false;
    };
  }, [openId, toast]);
  const tab = values.status === "pending_approval" ? "waiting" : "all";
  const waitingCount = waiting.data ? `${waiting.data.items.length}${waiting.data.has_more ? "+" : ""}` : null;
  const paused = me.merchant.payouts_paused;
  const inReview = !!me.merchant.in_review;

  const refresh = () => {
    void list.reload();
    void waiting.reload();
  };

  const columns: Column<Payout>[] = [
    { key: "time", header: t.payouts.cols.created, cell: (p) => <Time iso={p.created_at} />, mobile: "title" },
    {
      key: "ref", header: t.payouts.cols.playerRef, cell: (p) => (
        <span className="stack-cell">
          <Player refId={p.player_ref} name={p.player_name} />
          <small className="muted">{p.created_by ? fmt(t.payouts.byUser, { name: p.created_by }) : t.payouts.viaApi}</small>
        </span>
      ),
    },
    { key: "asset", header: t.common.asset, cell: (p) => <AssetChip asset={p.asset} chain={p.chain} compact /> },
    { key: "to", header: t.payouts.cols.to, cell: (p) => <Hash value={p.to} />, mobile: "hide" },
    { key: "amount", header: t.payouts.cols.amount, align: "right", cell: (p) => <strong className="amount" title={exactUnits(p.amount, p.decimals)}>{formatUnits(p.amount, p.decimals)}</strong> },
    { key: "fee", header: t.payouts.cols.fee, align: "right", cell: (p) => <span className="amount muted" title={exactUnits(p.fee, p.decimals)}>{formatUnits(p.fee, p.decimals)}</span>, mobile: "hide" },
    {
      key: "status", header: t.common.status, cell: (p) => (
        <span className="status-stack">
          <StatusPill status={p.status} />
          {p.waiting_reason && <small className="waiting-text" title={p.waiting_reason}>{t.vault.waitingLabel}: {waitingText(p)}</small>}
        </span>
      ),
    },
    { key: "tx", header: t.payouts.cols.txHash, cell: (p) => <Hash value={p.tx_hash} href={p.explorer_url} />, mobile: "hide" },
  ];

  const filtered = !!(values.q || values.status || values.asset || values.from || values.to);

  return (
    <div className="page">
      <PageHeader
        title={t.payouts.title}
        actions={
          <>
            {can("payouts:export") && (
              <button
                type="button" className="btn btn-ghost"
                onClick={() => downloadCsv("/payouts/export.csv", values, `payouts-${new Date().toISOString().slice(0, 10)}.csv`).catch((e) => toast(errorMessage(e), "error"))}
              >
                <IconDownload size={18} /> {t.common.exportCsv}
              </button>
            )}
            {can("payouts:create") && (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)} disabled={paused || inReview}
                title={inReview ? t.shell.inReviewHint : paused ? t.payouts.pausedTitle : undefined}>
                <IconPlus size={18} /> {t.payouts.newPayout}
              </button>
            )}
          </>
        }
      >
        {t.payouts.intro}
      </PageHeader>

      <div className="tabs" role="tablist" aria-label={t.payouts.viewsLabel}>
        <button type="button" role="tab" aria-selected={tab === "all"} className="tab" onClick={() => set("status", "")}>{t.payouts.tabAll}</button>
        <button type="button" role="tab" aria-selected={tab === "waiting"} className="tab" onClick={() => set("status", "pending_approval")}>
          {t.payouts.tabWaiting} {waitingCount && waitingCount !== "0" && <span className="tab-count">{waitingCount}</span>}
        </button>
      </div>

      <FilterBar
        q={values.q ?? ""} onQ={(v) => set("q", v)} searchLabel={t.payouts.searchLabel} hint={t.payouts.searchHint}
        status={values.status ?? ""} onStatus={(v) => set("status", v)} statuses={STATUSES} hideStatus={tab === "waiting"}
        asset={values.asset ?? ""} onAsset={(v) => set("asset", v)} assets={assets}
      />
      <DateRange from={values.from ?? ""} to={values.to ?? ""} onChange={(from, to) => setMany({ from, to })} />

      {list.error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void list.reload()}>{t.common.retry}</button>}>{errorMessage(list.error)}</Alert>
      ) : (
        <>
          <DataTable
            caption={t.payouts.tableCaption}
            columns={columns}
            rows={list.items}
            rowKey={(p) => p.id}
            loading={list.loading}
            onRowClick={setOpen}
            rowLabel={(p) => fmt(t.payouts.rowLabel, { amount: formatUnits(p.amount, p.decimals), token: tokenOf(p.asset), status: p.status })}
            footer={<Pager page={list.page} limit={list.limit} count={list.items.length} total={list.total} hasMore={list.hasMore} onPage={list.setPage} onLimit={list.setLimit} />}
            empty={
              tab === "waiting" && !values.q && !values.asset && !values.from ? (
                <EmptyState title={t.payouts.emptyWaitingTitle} art>{t.payouts.emptyWaitingBody}</EmptyState>
              ) : (
                <EmptyState title={filtered ? t.payouts.emptyNoMatch : t.payouts.emptyNone} art={!filtered}>
                  {filtered ? fmt(t.payouts.emptyFilteredBody, { hint: t.payouts.searchHint }) : t.payouts.emptyBody}
                </EmptyState>
              )
            }
          />
        </>
      )}

      <PayoutDrawer
        telegramOn={!!telegram.data?.available && !!telegram.data.organization.enabled}
        payout={open}
        onClose={() => {
          setOpen(null);
          if (openId) set("open", "");
        }}
        onChanged={(p) => {
          list.replace(p);
          setOpen(p);
          void waiting.reload();
        }}
      />
      {creating && (
        <NewPayout
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            toast(p.status === "pending_approval" ? t.payouts.toastSubmitted : t.payouts.toastCreated);
            refresh();
            setCreating(false);
            setOpen(p);
          }}
        />
      )}
    </div>
  );
}

function PayoutDrawer({ payout, onClose, onChanged, telegramOn }: { payout: Payout | null; onClose: () => void; onChanged: (p: Payout) => void; telegramOn: boolean }) {
  const t = useT();
  const { can, me } = useSession();
  const toast = useToast();
  const [p, setP] = useState<Payout | null>(payout);
  const [mode, setMode] = useState<"view" | "approve" | "reject">("view");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    setP(payout);
    setMode("view");
    setCode("");
    setReason("");
    setError(null);
    setCodeError(null);
    if (!payout) return;
    let alive = true;
    api.get<Payout>(`/payouts/${encodeURIComponent(payout.id)}`).then((x) => alive && setP(x)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [payout]);

  const act = async () => {
    if (!p) return;
    if (mode === "reject" && !reason.trim()) return setError(t.payouts.drawer.reasonRequired);
    if (code.length !== 6) return setCodeError(t.payouts.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const body = mode === "approve" ? { code } : { reason: reason.trim(), code };
      const r = await api.post<Payout>(`/payouts/${encodeURIComponent(p.id)}/${mode}`, body);
      toast(mode === "approve" ? t.payouts.drawer.approvedToast : t.payouts.drawer.rejectedToast);
      setMode("view");
      setCode("");
      onChanged(r);
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.payouts.codeWrong);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const token = p ? tokenOf(p.asset) : "";
  const pending = p?.status === "pending_approval" && can("payouts:approve");
  // can_approve (server) folds in maker-checker, the 24 h cooldown and the payout brake; older servers omit it.
  const approvable = pending && p?.can_approve !== false;
  const canAct = pending;
  const own = p?.created_by && me && p.created_by === me.user.name;

  return (
    <Dialog open={!!payout} onClose={onClose} kind="drawer" title={t.payouts.drawer.title} description={p ? formatTimeFull(p.created_at) : undefined} busy={busy}>
      {p && (
        <>
          <div className="drawer-hero">
            <div>
              <div className="drawer-amount" title={exactUnits(p.amount, p.decimals)}>{formatUnits(p.amount, p.decimals)} <span>{token}</span></div>
              <AssetChip asset={p.asset} chain={p.chain} />
            </div>
            <StatusPill status={p.status} />
          </div>
          {p.status === "failed" && p.last_error && <Alert tone="error" title={t.payouts.drawer.failedTitle}>{p.last_error}</Alert>}
          {p.waiting_reason && (
            <Alert tone="warn" title={t.vault.waitingTitle}>
              <span title={p.waiting_reason}>{fmt(t.vault.waitingBody, { reason: waitingText(p) })}</span>
            </Alert>
          )}
          {p.status === "rejected" && <Alert tone="info" title={fmt(t.payouts.drawer.rejectedBy, { name: p.rejected_by ?? t.payouts.drawer.aTeamMember })}>{p.reject_reason}</Alert>}
          {p.status === "pending_approval" && !canAct && <Alert tone="warn">{t.payouts.drawer.waitingNote}</Alert>}
          {p.status === "pending_approval" && telegramOn && <p className="tg-note">{t.payouts.drawer.telegramNote}</p>}

          {canAct && mode === "view" && (
            <div className="approve-box">
              <div>
                <strong>{t.payouts.drawer.needsApproval}</strong>
                <span>
                  {approvable
                    ? t.payouts.drawer.checkThenAct
                    : own
                      ? t.payouts.drawer.ownCantApprove
                      : t.payouts.drawer.cantApproveNow}
                </span>
              </div>
              <div className="btn-row">
                {approvable && <button type="button" className="btn btn-primary" onClick={() => { setMode("approve"); setError(null); }}>{t.payouts.drawer.approve}</button>}
                <button type="button" className="btn btn-ghost btn-danger-ghost" onClick={() => { setMode("reject"); setError(null); }}>{t.payouts.drawer.reject}</button>
              </div>
            </div>
          )}
          {canAct && mode !== "view" && (
            <form className="approve-box approve-form form-stack" onSubmit={(e) => { e.preventDefault(); void act(); }} noValidate>
              <strong>{mode === "approve" ? fmt(t.payouts.drawer.approveConfirm, { amount: formatUnits(p.send_amount ?? p.amount, p.decimals), token }) : t.payouts.drawer.rejectConfirm}</strong>
              {mode === "reject" && (
                <div className="field">
                  <label htmlFor="reject-reason">{t.payouts.drawer.reason}</label>
                  <textarea id="reject-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-autofocus placeholder={t.payouts.drawer.reasonPlaceholder} />
                </div>
              )}
              <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} autoFocus={mode === "approve"} />
              {error && <Alert tone="error">{error}</Alert>}
              <div className="btn-row">
                <button type="submit" className={`btn ${mode === "approve" ? "btn-primary" : "btn-danger"}`} disabled={busy}>
                  {busy && <Spinner />} {mode === "approve" ? t.payouts.drawer.approvePayout : t.payouts.drawer.rejectPayout}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => { setMode("view"); setError(null); setCodeError(null); setCode(""); }} disabled={busy}>{t.common.cancel}</button>
              </div>
            </form>
          )}

          <Details
            rows={[
              [t.payouts.d.amount, `${formatUnits(p.amount, p.decimals)} ${token}`],
              [t.payouts.d.fee, `${formatUnits(p.fee, p.decimals)} ${token}`],
              [t.payouts.d.playerReceives, p.send_amount === null ? null : <strong>{formatUnits(p.send_amount, p.decimals)} {token}</strong>],
              [t.payouts.d.totalFromBalance, p.total === null ? null : `${formatUnits(p.total, p.decimals)} ${token}`],
              [t.payouts.d.feeMode, p.fee_mode === "on_top" ? t.payouts.feeModes.on_top : p.fee_mode === "deducted" ? t.payouts.feeModes.deducted : p.fee_mode],
              [t.payouts.d.network, chainName(p.chain)],
              [t.payouts.d.toAddress, <Hash value={p.to} full />, true],
              [t.payouts.d.txHash, p.tx_hash ? <Hash value={p.tx_hash} full /> : null, true],
              [t.payouts.d.playerRef, p.player_ref ? <Player refId={p.player_ref} name={p.player_name} copy /> : null],
              [t.payouts.d.createdBy, p.created_by ?? "API"],
              [t.payouts.d.approvedBy, p.approved_by ? <>{p.approved_by}{p.approved_at && <> · <Time iso={p.approved_at} /></>}</> : null],
              [t.payouts.d.rejectedBy, p.rejected_by],
              [t.payouts.d.created, <Time iso={p.created_at} full />],
              [t.payouts.d.completed, p.completed_at ? <Time iso={p.completed_at} full /> : null],
              [t.payouts.d.requestId, <Hash value={p.request_id} full />, true],
              [t.payouts.d.payoutId, <Hash value={p.id} full />, true],
            ].filter(([label, v]) => v !== null || label === t.payouts.d.playerRef) as [ReactNode, ReactNode, boolean?][]}
          />
          {p.explorer_url && (
            <div className="drawer-actions">
              <ExternalLink href={p.explorer_url}>{t.payouts.drawer.viewOnExplorer}</ExternalLink>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}
